import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { Select, Row, Col, Spin, Space, Empty, Progress, Tag } from "antd";
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid,
    Tooltip, ResponsiveContainer, Cell, LabelList,
} from "recharts";
import {
    CalendarOutlined, CheckCircleOutlined, CloseCircleOutlined,
    ClockCircleOutlined, RiseOutlined, ExclamationCircleOutlined,
    BankOutlined, FilterOutlined, LogoutOutlined, AppstoreOutlined,
} from "@ant-design/icons";
import { AuthContext } from "../../context/AuthContext";

const { Option } = Select;

const MONTHS = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
];
const now = new Date();
const CURRENT_YEAR = now.getFullYear();
const YEARS = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - 2 + i);

export default function AttendanceReport() {
    const { authData, backUrl } = useContext(AuthContext);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [month, setMonth] = useState(now.getMonth() + 1);
    const [year, setYear] = useState(CURRENT_YEAR);
    const [organisations, setOrganisations] = useState([]);
    const [organisationId, setOrganisationId] = useState("ALL");
    const [departments, setDepartments] = useState([]);
    const [departmentId, setDepartmentId] = useState("ALL");

    useEffect(() => {
        axios.get(`${backUrl}/api/emp/organisations/`, {
            headers: { Authorization: `Bearer ${authData?.access}` },
        }).then(res => setOrganisations(res.data.results || res.data || []))
          .catch(() => {});
    }, [backUrl, authData?.access]);

    useEffect(() => {
        const q = organisationId !== "ALL" ? `?organisation=${organisationId}` : "";
        axios.get(`${backUrl}/api/emp/departments/${q}`, {
            headers: { Authorization: `Bearer ${authData?.access}` },
        }).then(res => {
            setDepartments(res.data.results || res.data || []);
            setDepartmentId("ALL");
        }).catch(() => setDepartments([]));
    }, [organisationId, backUrl, authData?.access]);

    useEffect(() => {
        setLoading(true);
        const params = { month, year };
        if (organisationId !== "ALL") params.organisation_id = organisationId;
        if (departmentId !== "ALL") params.department_id = departmentId;

        axios.get(`${backUrl}/api/reports/attendance-summary/`, {
            params,
            headers: { Authorization: `Bearer ${authData?.access}` },
        }).then(res => setData(res.data || null))
          .catch(() => setData(null))
          .finally(() => setLoading(false));
    }, [organisationId, departmentId, month, year, backUrl, authData?.access]);

    const presentPct     = Number(data?.present_percentage || 0);
    const absentPct      = Number(data?.absent_percentage  || 0);
    const leavePct       = Number(data?.leave_percentage   || 0);
    const latePct        = Number(data?.late_percentage    || 0);
    const lateDays       = Number(data?.late_days ?? data?.late_count ?? 0);
    const lateInDays     = Number(data?.late_in_days      || 0);
    const graceLateDays  = Number(data?.grace_late_days   || 0);
    const earlyOutDays   = Number(data?.early_out_days    || 0);
    const graceEarlyDays = Number(data?.grace_early_days  || 0);
    const nlbDays        = Number(data?.nlb_days ?? data?.nlb_count ?? 0);
    const otHours        = Number(data?.overtime_hours     || 0);

    const kpis = [
        {
            label: "Present Rate",
            val: `${presentPct}%`,
            icon: <CheckCircleOutlined />,
            color: "#10b981", bg: "#f0fdf4", border: "#bbf7d0",
            progress: presentPct, showProgress: true,
            subText: "Overall Attendance"
        },
        {
            label: "Absent Rate",
            val: `${absentPct}%`,
            icon: <CloseCircleOutlined />,
            color: "#ef4444", bg: "#fff1f2", border: "#fecaca",
            progress: absentPct, showProgress: true,
            subText: "Unexcused Absence"
        },
        {
            label: "Late Days",
            val: lateDays,
            icon: <ClockCircleOutlined />,
            color: "#f59e0b", bg: "#fffbeb", border: "#fde68a",
            showProgress: false,
            subTags: [
                { label: `Late: ${lateInDays}`, color: "#d97706" },
                { label: `Grace: ${graceLateDays}`, color: "#b45309" }
            ]
        },
        {
            label: "Early Out Days",
            val: earlyOutDays + graceEarlyDays,
            icon: <LogoutOutlined />,
            color: "#8b5cf6", bg: "#f5f3ff", border: "#ddd6fe",
            showProgress: false,
            subTags: [
                { label: `Early: ${earlyOutDays}`, color: "#7c3aed" },
                { label: `Grace: ${graceEarlyDays}`, color: "#6d28d9" }
            ]
        },
        {
            label: "NLB Days",
            val: nlbDays,
            icon: <ExclamationCircleOutlined />,
            color: "#bf4406", bg: "#fff1f2", border: "#fecaca",
            showProgress: false,
            subText: "No Pay / LOP"
        },
        {
            label: "OT Hours",
            val: `${otHours}h`,
            icon: <RiseOutlined />,
            color: "#0e497b", bg: "#eff6ff", border: "#bfdbfe",
            showProgress: false,
            subText: "Overtime Work"
        },
    ];

    const chartData = data ? [
        { name: "Present Rate", value: presentPct, fill: "#10b981", label: `${presentPct}%` },
        { name: "Absent Rate",  value: absentPct,  fill: "#ef4444", label: `${absentPct}%` },
        { name: "Late Rate",    value: latePct,    fill: "#f59e0b", label: `${latePct}%` },
        { name: "On Leave",     value: leavePct,   fill: "#CB00CC", label: `${leavePct}%` },
    ] : [];

    return (
        <div style={{
            background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
            borderRadius: 16, border: "1px solid #e2e8f0",
            boxShadow: "0 4px 16px rgba(15,23,42,0.04)",
            position: "relative", height: "100%",
        }}>
            <div style={{ height: 3.5, background: "linear-gradient(90deg, #0e497b, #10b981)", borderTopLeftRadius: 16, borderTopRightRadius: 16 }} />
            <div style={{ padding: "16px 18px" }}>
                {/* Section Header */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{
                            width: 36, height: 36, borderRadius: 10,
                            background: "linear-gradient(135deg, #0e497b 0%, #1e40af 100%)",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            color: "#fff", boxShadow: "0 2px 8px rgba(14,73,123,0.25)", flexShrink: 0
                        }}>
                            <CalendarOutlined style={{ fontSize: 17 }} />
                        </div>
                        <div>
                            <div style={{ fontWeight: 800, fontSize: 15, color: "#0f172a", lineHeight: 1.2 }}>
                                Monthly Attendance Summary
                            </div>
                            <div style={{ fontSize: 11.5, color: "#64748b", marginTop: 2 }}>
                                {MONTHS[month - 1]} {year} &middot; Attendance KPIs & Analytics
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filter Grid - Dropdowns allow floating overlays without clipping */}
                <Row gutter={[8, 8]} style={{ marginBottom: 14 }}>
                    <Col xs={12} sm={6}>
                        <Select
                            value={organisationId}
                            onChange={(v) => setOrganisationId(v || "ALL")}
                            showSearch
                            optionFilterProp="children"
                            allowClear
                            onClear={() => setOrganisationId("ALL")}
                            placeholder="Organisation"
                            style={{ width: "100%" }}
                            size="middle"
                            popupMatchSelectWidth={false}
                            dropdownMatchSelectWidth={false}
                            getPopupContainer={triggerNode => triggerNode.parentElement}
                        >
                            <Option value="ALL">
                                <Space size={4}><BankOutlined style={{ color: "#0e497b" }} /> <span>All Orgs ({organisations.length})</span></Space>
                            </Option>
                            {organisations.map(o => (
                                <Option key={o.id} value={o.id}>
                                    <Space size={4}><BankOutlined style={{ color: "#0e497b" }} /> <span>{o.name}</span></Space>
                                </Option>
                            ))}
                        </Select>
                    </Col>

                    <Col xs={12} sm={6}>
                        <Select
                            value={departmentId}
                            onChange={(v) => setDepartmentId(v || "ALL")}
                            showSearch
                            optionFilterProp="children"
                            allowClear
                            onClear={() => setDepartmentId("ALL")}
                            placeholder="Department"
                            style={{ width: "100%" }}
                            size="middle"
                            popupMatchSelectWidth={false}
                            dropdownMatchSelectWidth={false}
                            getPopupContainer={triggerNode => triggerNode.parentElement}
                        >
                            <Option value="ALL">
                                <Space size={4}><AppstoreOutlined style={{ color: "#0e497b" }} /> <span>All Depts ({departments.length})</span></Space>
                            </Option>
                            {departments.map(d => (
                                <Option key={d.id} value={d.id}>
                                    <Space size={4}><AppstoreOutlined style={{ color: "#0e497b" }} /> <span>{d.name}</span></Space>
                                </Option>
                            ))}
                        </Select>
                    </Col>

                    <Col xs={12} sm={6}>
                        <Select
                            value={month}
                            onChange={setMonth}
                            style={{ width: "100%" }}
                            size="middle"
                            popupMatchSelectWidth={false}
                            dropdownMatchSelectWidth={false}
                            getPopupContainer={triggerNode => triggerNode.parentElement}
                        >
                            {MONTHS.map((m, i) => (
                                <Option key={i + 1} value={i + 1}>
                                    <Space size={4}><CalendarOutlined style={{ color: "#2563eb" }} /> <span>{m}</span></Space>
                                </Option>
                            ))}
                        </Select>
                    </Col>

                    <Col xs={12} sm={6}>
                        <Select
                            value={year}
                            onChange={setYear}
                            style={{ width: "100%" }}
                            size="middle"
                            popupMatchSelectWidth={false}
                            dropdownMatchSelectWidth={false}
                            getPopupContainer={triggerNode => triggerNode.parentElement}
                        >
                            {YEARS.map(y => (
                                <Option key={y} value={y}>
                                    <Space size={4}><FilterOutlined style={{ color: "#7c3aed" }} /> <span>{y}</span></Space>
                                </Option>
                            ))}
                        </Select>
                    </Col>
                </Row>

                {loading ? (
                    <div style={{ textAlign: "center", padding: "50px 0" }}>
                        <Spin size="large" />
                        <div style={{ marginTop: 12, color: "#64748b", fontWeight: 600 }}>
                            Loading Attendance Summary...
                        </div>
                    </div>
                ) : !data ? (
                    <Empty description="No attendance summary data available" style={{ padding: "40px 0" }} />
                ) : (
                    <>
                        {/* KPI Cards — 2 rows x 3 cards */}
                        <Row gutter={[10, 10]} style={{ marginBottom: 16 }}>
                            {kpis.map(k => (
                                <Col xs={12} sm={8} key={k.label}>
                                    <div style={{
                                        background: k.bg, border: `1px solid ${k.border}`,
                                        borderRadius: 12, padding: "10px 12px", height: "100%", minHeight: 96,
                                        display: "flex", flexDirection: "column", justifyContent: "space-between",
                                        boxShadow: "0 2px 6px rgba(0,0,0,0.02)"
                                    }}>
                                        <div>
                                            <div style={{
                                                display: "flex", alignItems: "center", gap: 5,
                                                marginBottom: 4,
                                            }}>
                                                <span style={{ color: k.color, fontSize: 13 }}>{k.icon}</span>
                                                <span style={{
                                                    fontSize: 10.5, fontWeight: 800, color: k.color,
                                                    textTransform: "uppercase", letterSpacing: "0.03em"
                                                }}>
                                                    {k.label}
                                                </span>
                                            </div>
                                            <div style={{ fontSize: 21, fontWeight: 900, color: k.color, lineHeight: 1.1 }}>
                                                {k.val}
                                            </div>
                                        </div>

                                        {k.showProgress && (
                                            <div style={{ marginTop: 6 }}>
                                                <Progress
                                                    percent={Math.min(k.progress, 100)}
                                                    strokeColor={k.color}
                                                    trailColor="#e2e8f0"
                                                    showInfo={false}
                                                    size={[undefined, 5]}
                                                    style={{ margin: 0 }}
                                                />
                                            </div>
                                        )}

                                        {k.subTags && (
                                            <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 6 }}>
                                                {k.subTags.map(st => (
                                                    <Tag key={st.label} style={{
                                                        fontSize: 9.5, fontWeight: 700, margin: 0, padding: "1px 5px",
                                                        borderRadius: 4, color: st.color, border: `1px solid ${st.color}40`,
                                                        background: "#ffffff90"
                                                    }}>
                                                        {st.label}
                                                    </Tag>
                                                ))}
                                            </div>
                                        )}

                                        {k.subText && !k.showProgress && !k.subTags && (
                                            <div style={{ fontSize: 9.5, color: "#64748b", marginTop: 4, fontWeight: 600 }}>
                                                {k.subText}
                                            </div>
                                        )}
                                    </div>
                                </Col>
                            ))}
                        </Row>

                        {/* Enhanced Recharts Visualization */}
                        {chartData.length > 0 && (
                            <div style={{
                                background: "#fafafa", borderRadius: 12, border: "1px solid #f1f5f9",
                                padding: "12px 10px 6px 10px", marginTop: 4
                            }}>
                                <ResponsiveContainer width="100%" height={170}>
                                    <BarChart data={chartData} margin={{ top: 18, right: 15, left: -20, bottom: 0 }} barSize={34}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                                        <XAxis dataKey="name" tick={{ fill: "#475569", fontSize: 11, fontWeight: 700 }} tickLine={false} axisLine={{ stroke: "#cbd5e1" }} />
                                        <YAxis domain={[0, 100]} tick={{ fill: "#64748b", fontSize: 10 }} tickFormatter={v => `${v}%`} axisLine={false} tickLine={false} />
                                        <Tooltip
                                            content={({ active, payload }) => {
                                                if (!active || !payload?.length) return null;
                                                const d = payload[0];
                                                return (
                                                    <div style={{
                                                        background: "rgba(15, 23, 42, 0.92)",
                                                        color: "#fff", padding: "6px 12px", borderRadius: 8,
                                                        fontSize: 12, fontWeight: 700, boxShadow: "0 4px 12px rgba(0,0,0,0.15)"
                                                    }}>
                                                        <span style={{ color: d.payload.fill }}>● </span>
                                                        {d.name}: {d.value}%
                                                    </div>
                                                );
                                            }}
                                        />
                                        <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                                            {chartData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={entry.fill} />
                                            ))}
                                            <LabelList dataKey="label" position="top" style={{ fill: "#334155", fontSize: 10, fontWeight: 800 }} />
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}


